// src/pages/MapBetaPage.jsx
import React, { useEffect, useState, useMemo, useRef } from "react";
import { Container, Row, Col, Form, Button, Spinner, Alert, Modal, Collapse  } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import { MapContainer, TileLayer, Circle, Marker, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { geocodeLocation } from "../utils/geocode";

import { supabase } from "../supabaseClient";

function MapViewUpdater({ center, zoom }) {
  const map = useMap();

  useEffect(() => {
    if (!center || center.length !== 2) return;
    map.setView(center, zoom);
  }, [map, center, zoom]);

  return null;
}

// Leaflet default ikonok beállítása bundlerhez

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [22, 36],
  iconAnchor: [11, 36],
  popupAnchor: [0, -30],
  tooltipAnchor: [10, -28],
  shadowSize: [36, 36],
});

const getPlaceMarkerIcon = (count, isSelected = false) => {
  let tier = "small";
  if (count >= 5 && count < 10) tier = "medium";
  else if (count >= 10) tier = "large";

  const extraClass = isSelected ? " place-marker--selected" : "";

  return L.divIcon({
    className: `place-marker place-marker--${tier}${extraClass}`,
    html: `<div class="place-marker__inner">${count}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
  });
};

const defaultCenter = [47.1625, 19.5033]; // Magyarország közepe nagyjából
const defaultZoom = 7;
const RADIUS_KM = 50;
//const CANONICAL_BASE = "https://lelkigyakorlatok.vercel.app";
const LOCATIONIQ_TOKEN = process.env.REACT_APP_LOCATIONIQ_TOKEN;


// Célcsoportok a szűrőhöz
const targetGroups = [
  "Mindenki",
  "Fiatalok",
  "Idősek",
  "Fiatal házasok",
  "Érett házasok",
  "Jegyesek",
  "Tinédzserek",
  "Családok",
  "Férfiak",
  "Nők",
];

function toRad(deg) {
  return (deg * Math.PI) / 180;
}

function distanceInKm(lat1, lng1, lat2, lng2) {
  const R = 6371; // Föld sugara km-ben
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function formatDateRange(startISO, endISO) {
  if (!startISO) return "";
  const start = new Date(startISO);
  const end = endISO ? new Date(endISO) : null;
  const opts = { year: "numeric", month: "2-digit", day: "2-digit" };

  if (!end) return start.toLocaleDateString("hu-HU", opts);

  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();

  if (sameDay) return start.toLocaleDateString("hu-HU", opts);

  return `${start.toLocaleDateString("hu-HU", opts)} – ${end.toLocaleDateString("hu-HU", opts)}`;
}
function buildLandingUrl(ev) {
  if (!ev || !ev.id) return "/";
  // Itt állítsd be, milyen útvonalra mutasson az esemény
  // Ha nálad pl. /events/:id az útvonal:
  return `/events/${ev.id}`;
}

const MapBetaPage = ({ hideAdmin = false }) => {

  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [filter, setFilter] = useState("Mindenki");
  const [sourceFilter, setSourceFilter] = useState("mind");
  const [q, setQ] = useState("");
  const [month, setMonth] = useState("osszes");
  const [filtersOpen, setFiltersOpen] = useState(true); // mobilon összecsukható szűrők
//const [timeWindow, setTimeWindow] = useState("all");  // "all" | "30" | "90"



const [selectedGroup, setSelectedGroup] = useState(null);
const [showGroupModal, setShowGroupModal] = useState(false);

const [selectedEvent, setSelectedEvent] = useState(null);
const [showEventModal, setShowEventModal] = useState(false);
  const [radiusEnabled, setRadiusEnabled] = useState(false);
  const [radiusCenter, setRadiusCenter] = useState(null); // { lat, lng }
  const [locationQuery, setLocationQuery] = useState("");
  const [geoStatus, setGeoStatus] = useState("");
  const [geoError, setGeoError] = useState("");
  const [isGeoAdminBusy, setIsGeoAdminBusy] = useState(false);
  const [geoAdminMessage, setGeoAdminMessage] = useState("");
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(defaultZoom);
const [timeWindow, setTimeWindow] = useState("all");  // "all" | "30" | "90"

   const mapRef = useRef(null);

  const resetFilters = () => {
    setFilter("Mindenki");
    setSourceFilter("mind");
    setQ("");
    setMonth("osszes");
    setTimeWindow("all");
    setRadiusEnabled(false);
    setRadiusCenter(null);
    setLocationQuery("");
    setMapCenter(defaultCenter);
    setMapZoom(defaultZoom);
  };

  const location = useLocation();

  // Események betöltése Supabase-ből, lat/lng-vel együtt
  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      setError("");

      const { data, error: fetchError } = await supabase
        .from("events")
        .select(
          `
          id, title, location, description,
          start_date, end_date,
          contact, registration_link,
          target_group, source, created_by,
          image_url, poster_url,
          lat, lng,
          communities (id, name)
        `
        )
        .gte("start_date", new Date().toISOString())
        .order("start_date", { ascending: true });

      if (fetchError) {
        console.error("Error loading events for map", fetchError);
        setError("Nem sikerült betölteni az eseményeket.");
        setEvents([]);
      } else {
        setEvents(data || []);
      }

      setLoading(false);
    };

    fetchEvents();
  }, []);

  // SZŰRT ESEMÉNYEK – minden szűrés itt történik
  const filteredEvents = useMemo(() => {
    const needle = q.trim().toLowerCase();

    return events.filter((ev) => {
      const lat = ev.lat == null || ev.lat === "" ? NaN : Number(ev.lat);
      const lng = ev.lng == null || ev.lng === "" ? NaN : Number(ev.lng);

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;

      // célcsoport
      if (filter !== "Mindenki") {
        if (ev.target_group !== filter) return false;
      }

      // forrás
      const isManual = !!ev.created_by;
      const isAuto = !!ev.source && !ev.created_by;
      if (sourceFilter === "kezi" && !isManual) return false;
      if (sourceFilter === "auto" && !isAuto) return false;

      // kereső
      if (needle) {
        const t = (ev.title || "").toLowerCase();
        const l = (ev.location || "").toLowerCase();
        if (!t.includes(needle) && !l.includes(needle)) return false;
      }

      // hónap
      if (month !== "osszes" && ev.start_date) {
        const m = new Date(ev.start_date).getMonth() + 1;
        if (String(m) !== month) return false;
      }

      // időablak (30 / 90 nap)
      if (timeWindow !== "all" && ev.start_date) {
        const now = new Date();
        const start = new Date(ev.start_date);
        const diffDays =
          (start.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);

        if (timeWindow === "30" && diffDays > 30) return false;
        if (timeWindow === "90" && diffDays > 90) return false;
      }

      // 50 km-es körzet
      if (radiusEnabled && radiusCenter) {
        const d = distanceInKm(radiusCenter.lat, radiusCenter.lng, lat, lng);
        if (!Number.isFinite(d) || d > RADIUS_KM) return false;
      }

      return true;
    });
  }, [
    events,
    filter,
    sourceFilter,
    q,
    month,
    timeWindow,
    radiusEnabled,
    radiusCenter,
  ]);

  // MARKER CSOPORTOK – azonos ház azonos kulcs
  const markerGroups = useMemo(() => {
    const groups = new Map();

    filteredEvents.forEach((ev) => {
      const lat = ev.lat == null || ev.lat === "" ? NaN : Number(ev.lat);
      const lng = ev.lng == null || ev.lng === "" ? NaN : Number(ev.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

      const key = `${lat.toFixed(5)}_${lng.toFixed(5)}`;

      if (!groups.has(key)) {
        groups.set(key, {
          lat,
          lng,
          events: [],
        });
      }
      groups.get(key).events.push(ev);
    });

    groups.forEach((group) => {
      group.events.sort(
        (a, b) => new Date(a.start_date || 0) - new Date(b.start_date || 0)
      );
    });

    return Array.from(groups.values());
  }, [filteredEvents]);

  // HELYSZÍN LISTA – bal oldali "Lelkigyakorlatos házak"
  const placeList = useMemo(() => {
    return markerGroups
      .map((group) => {
        const eventsAtPlace = group.events || [];
        if (!eventsAtPlace.length) return null;

        const firstEvent = eventsAtPlace[0];

        const upcoming = [...eventsAtPlace].sort(
          (a, b) => new Date(a.start_date || 0) - new Date(b.start_date || 0)
        )[0];

        return {
          id: firstEvent.id,
          location: firstEvent.location || "Ismeretlen helyszín",
          lat: group.lat,
          lng: group.lng,
          count: eventsAtPlace.length,
          nextDate: upcoming?.start_date || null,
        };
      })
      .filter(Boolean)
      .sort((a, b) =>
        a.location.localeCompare(b.location, "hu", { sensitivity: "base" })
      );
  }, [markerGroups]);

  // ADMIN: koordináta nélküli események listája
  const eventsWithoutCoords = useMemo(
    () =>
      events.filter((ev) => {
        const lat = ev.lat == null || ev.lat === "" ? NaN : Number(ev.lat);
        const lng = ev.lng == null || ev.lng === "" ? NaN : Number(ev.lng);
        return !Number.isFinite(lat) || !Number.isFinite(lng);
      }),
    [events]
  );

  // DEEP LINK: /terkep?event=ID → rázoomol
  useEffect(() => {
  const params = new URLSearchParams(location.search);
  const eventId = params.get("event");
  if (!eventId) return;
  if (!events || events.length === 0) return;

  const ev = events.find((e) => String(e.id) === String(eventId));
  if (!ev) return;

  // 1) térkép rázoomol az esemény koordinátáira
  if (ev.lat != null && ev.lng != null) {
    const lat = Number(ev.lat);
    const lng = Number(ev.lng);

    setMapCenter([lat, lng]);
    setMapZoom(11);

    if (mapRef.current && mapRef.current.setView) {
      mapRef.current.setView([lat, lng], 11);
    }
  }

  // 2) ha van olyan markerGroup, ami tartalmazza ezt az eseményt,
  //    azt beállítjuk selectedGroup-nak (marker kiemeléshez / későbbi kattintáshoz)
  if (markerGroups && markerGroups.length > 0) {
    const group = markerGroups.find((g) =>
      g.events.some((e) => String(e.id) === String(eventId))
    );

    if (group) {
      setSelectedGroup(group);
      // NEM nyitunk modált itt!
      return;
    }
  }

  // 3) fallback: ha valamiért nincs group, elmentjük az eseményt,
  //    de modált itt SEM nyitunk automatikusan
  setSelectedEvent(ev);
}, [location.search, events, markerGroups]);


		  const handlePlaceClick = (place) => {
			// ehhez a helyszínhez tartozó group megkeresése
			const group = markerGroups.find(
			  (g) => g.lat === place.lat && g.lng === place.lng
			);

			if (group) {
			  setSelectedGroup(group);
			  setShowGroupModal(true);
			}
			
			
			  // opcionálisan: map center / zoom beállítás
			  if (mapRef.current) {
				mapRef.current.setView([place.lat, place.lng], 10);
			  }
		  };

  // Szűrés a saját pozíciód alapján
  const handleUseMyLocation = () => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setGeoError("A böngésződ nem támogatja vagy tiltja a helymeghatározást.");
      return;
    }
    setGeoError("");
    setGeoStatus("Pozíció meghatározása...");

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const center = { lat: latitude, lng: longitude };
        setRadiusCenter(center);
        setRadiusEnabled(true);
        setGeoStatus("Pozíció beállítva (50 km-es körzet).");

        setMapCenter([center.lat, center.lng]);
        setMapZoom(9);

        const map = mapRef.current;
        if (map && map.setView) {
          map.setView([center.lat, center.lng], 9);
        }
      },
      () => {
        setGeoStatus("");
        setGeoError("Nem sikerült lekérni a pozíciódat.");
      },
      { enableHighAccuracy: true }
    );
  };

  // Szűrés megadott település alapján (LocationIQ)
  const handleGeocodeSearch = async (e) => {
    e.preventDefault();
    const query = locationQuery.trim();
    if (!query) return;

    setGeoError("");
    setGeoStatus("Hely keresése LocationIQ-val...");

    try {
      if (!LOCATIONIQ_TOKEN) {
        setGeoStatus("");
        setGeoError("Hiányzik a LocationIQ token (REACT_APP_LOCATIONIQ_TOKEN).");
        return;
      }

      const url = `https://eu1.locationiq.com/v1/search?key=${LOCATIONIQ_TOKEN}&q=${encodeURIComponent(
        query
      )}&format=json&limit=1`;

      const resp = await fetch(url);
      if (!resp.ok) {
        throw new Error("Geocoding hiba");
      }
      const json = await resp.json();
      if (!Array.isArray(json) || json.length === 0) {
        throw new Error("Nincs találat erre a helyre.");
      }

      const first = json[0];
      const lat = parseFloat(first.lat);
      const lng = parseFloat(first.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        throw new Error("Érvénytelen koordináták.");
      }

      const center = { lat, lng };
      setRadiusCenter(center);
      setRadiusEnabled(true);
      setGeoStatus(`Középpont beállítva: ${first.display_name || query}`);

      setMapCenter([center.lat, center.lng]);
      setMapZoom(9);

      const map = mapRef.current;
      if (map && map.setView) {
        map.setView([center.lat, center.lng], 9);
      }
    } catch (err) {
      console.error(err);
      setGeoStatus("");
      setGeoError("Nem sikerült megtalálni ezt a helyet.");
    }
  };

  const handleClearRadius = () => {
    setRadiusEnabled(false);
    setRadiusCenter(null);
    setGeoStatus("");
    setGeoError("");
    setMapCenter(defaultCenter);
    setMapZoom(defaultZoom);

    const map = mapRef.current;
    if (map && map.setView) {
      map.setView(defaultCenter, defaultZoom);
    }
  };
  
 
const geocodeEvent = async (ev) => {
  if (!ev.location) {
    setGeoAdminMessage("Ehhez az eseményhez nincs location megadva.");
    return;
  }

  try {
    setIsGeoAdminBusy(true);
    setGeoAdminMessage(`Geokódolás: ${ev.title}...`);

    // geokódolás a közös helperrel
    const coords = await geocodeLocation(ev.location);
    if (!coords) {
      setGeoAdminMessage("Nem sikerült geokódolni ezt a helyszínt.");
      return;
    }

    const { lat, lng } = coords;

    // Supabase UPDATE – ha RLS engedi, error null lesz
    const { error: updateError } = await supabase
      .from("events")
      .update({ lat, lng })
      .eq("id", ev.id);

    if (updateError) {
      console.error("Supabase update error", updateError);
      setGeoAdminMessage(`Supabase hiba: ${updateError.message}`);
      return;
    }

    // Ha id stimmel és az RLS engedi, itt már frissült a DB.
    // Frissítjük a lokális state-et is, hogy azonnal látszódjon a marker.
    setEvents((prev) =>
      prev.map((e) => (e.id === ev.id ? { ...e, lat, lng } : e))
    );

    setGeoAdminMessage(`Koordináták frissítve: ${ev.title}`);
  } catch (err) {
    console.error("geocodeEvent error", err);
    setGeoAdminMessage(`Hiba: ${err.message}`);
  } finally {
    setIsGeoAdminBusy(false);
  }
};

  const months = [
    { value: "osszes", label: "Összes hónap" },
    { value: "1", label: "Jan" },
    { value: "2", label: "Feb" },
    { value: "3", label: "Már" },
    { value: "4", label: "Ápr" },
    { value: "5", label: "Máj" },
    { value: "6", label: "Jún" },
    { value: "7", label: "Júl" },
    { value: "8", label: "Aug" },
    { value: "9", label: "Szep" },
    { value: "10", label: "Okt" },
    { value: "11", label: "Nov" },
    { value: "12", label: "Dec" },
  ];

  return (
    <Container fluid className="mt-4">
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <div>
          <h1 className="h4 mb-1">Lelkigyakorlatok térképen (beta)</h1>
          <p className="text-muted mb-0">
            Csak azok az események látszanak, amelyekhez meg van adva pontos hely (koordináta).
          </p>
        </div>
        <Link to="/" className="btn btn-outline-secondary">
          &larr; Vissza a listához
        </Link>
      </div>
      <div className="d-md-none mb-2">
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => setFiltersOpen((prev) => !prev)}
        >
          Szűrők {filtersOpen ? "elrejtése" : "megjelenítése"}
        </Button>
      </div>
            <Row className="g-3">
        {/* Bal: szűrőpanel */}
        <Col md={3}>
          <Collapse in={filtersOpen} dimension="height">
            <div>
              <div className="card border-0 shadow-sm mb-3">
                <div className="card-body">
                  <h5 className="card-title mb-3">Szűrők</h5>

                  <Form.Group className="mb-3">
                    <Form.Label>Keresés cím / hely alapján</Form.Label>
                    <Form.Control
                      type="text"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="pl. nagyböjt, Budapest..."
                      size="sm"
                    />
                  </Form.Group>

                  <div className="mb-3">
                    <strong className="d-block mb-2">Célcsoport</strong>
                    <div className="d-flex flex-wrap gap-2">
                      {targetGroups.map((group) => (
                        <Button
                          key={group}
                          variant={filter === group ? "primary" : "outline-primary"}
                          size="sm"
                          onClick={() => setFilter(group)}
                        >
                          {group}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-3">
                    <strong className="d-block mb-2">Forrás</strong>
                    <div className="d-flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant={sourceFilter === "mind" ? "dark" : "outline-dark"}
                        onClick={() => setSourceFilter("mind")}
                      >
                        Mind
                      </Button>
                      <Button
                        size="sm"
                        variant={sourceFilter === "kezi" ? "primary" : "outline-primary"}
                        onClick={() => setSourceFilter("kezi")}
                      >
                        Kézi
                      </Button>
                      <Button
                        size="sm"
                        variant={sourceFilter === "auto" ? "secondary" : "outline-secondary"}
                        onClick={() => setSourceFilter("auto")}
                      >
                        Automatikus
                      </Button>
                    </div>
                  </div>

                  <div className="mb-3">
                    <strong className="d-block mb-2">Hónap</strong>
                    <div className="d-flex flex-wrap gap-2">
                      {months.map((m) => (
                        <Button
                          key={m.value}
                          size="sm"
                          variant={month === m.value ? "primary" : "outline-primary"}
                          onClick={() => setMonth(m.value)}
                        >
                          {m.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <Form.Group className="mb-3">
                    <Form.Label>Időablak</Form.Label>
                    <div className="d-flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant={timeWindow === "all" ? "primary" : "outline-primary"}
                        onClick={() => setTimeWindow("all")}
                      >
                        Összes
                      </Button>
                      <Button
                        size="sm"
                        variant={timeWindow === "30" ? "primary" : "outline-primary"}
                        onClick={() => setTimeWindow("30")}
                      >
                        30 nap
                      </Button>
                      <Button
                        size="sm"
                        variant={timeWindow === "90" ? "primary" : "outline-primary"}
                        onClick={() => setTimeWindow("90")}
                      >
                        90 nap
                      </Button>
                    </div>
                  </Form.Group>

                  <hr />

                  <div className="mb-2">
                    <strong className="d-block mb-2">Közeli lelkigyakorlatok</strong>
                    <div className="d-flex flex-column gap-2">
                      <Button size="sm" variant="outline-success" onClick={handleUseMyLocation}>
                        Saját pozícióm használata (50 km)
                      </Button>
                      <Form onSubmit={handleGeocodeSearch}>
                        <Form.Label className="small mb-1">Vagy adj meg egy települést</Form.Label>
                        <div className="d-flex gap-2">
                          <Form.Control
                            type="text"
                            size="sm"
                            value={locationQuery}
                            onChange={(e) => setLocationQuery(e.target.value)}
                            placeholder="pl. Győr, Kolozsvár..."
                          />
                          <Button type="submit" size="sm" variant="outline-secondary">
                            Keresés
                          </Button>
                        </div>
                      </Form>
                      {radiusEnabled && (
                        <Button
                          size="sm"
                          variant="outline-danger"
                          className="mt-1"
                          onClick={handleClearRadius}
                        >
                          Körzet szűrés kikapcsolása
                        </Button>
                      )}
                    </div>

                    <div className="small text-muted mt-2">
                      A megadott ponthoz kb. 50 km-es körben lévő lelkigyakorlatokat mutatjuk.
                      Ha kikapcsolod a körzet szűrést, ismét az összes, szűrésnek megfelelő
                      eseményt látod.
                    </div>

                    {radiusEnabled && radiusCenter && (
                      <div className="small text-muted mt-1">
                        Jelenleg {filteredEvents.length} esemény esik a körön belül.
                      </div>
                    )}

                    <hr />

                    <div className="d-flex justify-content-end">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={resetFilters}
                      >
                        Szűrők alaphelyzetbe
                      </Button>
                    </div>

                    <div className="mt-4">
                      <h5 className="mb-2">Lelkigyakorlatos házak</h5>
                      <div className="small text-muted mb-2">
                        {placeList.length} helyszín felel meg a szűrésnek
                      </div>

                      <div style={{ maxHeight: 260, overflowY: "auto" }}>
                        {placeList.length === 0 && (
                          <div className="text-muted small">
                            Nincs a szűrésnek megfelelő helyszín.
                          </div>
                        )}

                        {placeList.map((place) => (
                          <button
                            key={place.id}
                            type="button"
                            className="w-100 text-start btn btn-light mb-1"
                            onClick={() => handlePlaceClick(place)}
                          >
                            <div className="fw-semibold">{place.location}</div>
                            <div className="small text-muted d-flex justify-content-between">
                              <span>{place.count} esemény</span>
                              {place.nextDate && (
                                <span>
                                  {new Date(place.nextDate).toLocaleDateString("hu-HU", {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                  })}
                                </span>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {geoStatus && (
                      <p className="small text-muted mt-2 mb-0">{geoStatus}</p>
                    )}
                    {geoError && (
                      <p className="small text-danger mt-1 mb-0">
                        {geoError}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="small text-muted">
                <p className="mb-1">
                  Tipp: a markerre kattintva felugró ablakban látod az esemény fő adatait, és
                  megnyithatod részletesen az oldalon.
                </p>
              </div>
            </div>
          </Collapse>
        </Col>


        {/* Jobb: térkép */}
        <Col md={9}>
          {loading && (
            <div className="text-center py-5">
              <Spinner animation="border" role="status" />
            </div>
          )}

          {!loading && error && <Alert variant="danger">{error}</Alert>}

          {!loading && !error && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="small text-muted">
                  <strong>{filteredEvents.length}</strong> esemény ·{" "}
                  <strong>{placeList.length}</strong> helyszín
                  {radiusEnabled && radiusCenter && (
                    <>
                      {" "}
                      – <span>50 km-es körön belüli szűrés</span>
                    </>
                  )}
                </div>
              </div>
			<div style={{ position: "sticky", top: 80 }}>
				<MapContainer
				  center={mapCenter}
				  zoom={mapZoom}
				  style={{
					height: "calc(100vh - 120px)",
					width: "100%",
					borderRadius: "12px",
				  }}
				  whenCreated={(mapInstance) => {
					mapRef.current = mapInstance;
				  }}
				>
				 <MapViewUpdater center={mapCenter} zoom={mapZoom} />
				    <TileLayer
					  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
					  url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
					/>
					   {radiusEnabled && radiusCenter && (
						  <Circle
							center={[radiusCenter.lat, radiusCenter.lng]}
							radius={RADIUS_KM * 1000}
							pathOptions={{ color: "#0d6efd", fillOpacity: 0.1 }}
						  />
						)}

						{markerGroups.map((group, idx) => {
						  const count = group.events.length;
						  const isSelected =
							selectedGroup &&
							selectedGroup.lat === group.lat &&
							selectedGroup.lng === group.lng;

						  return (
							<Marker
							  key={group.events[0] ? group.events[0].id : idx}
							  position={[group.lat, group.lng]}
							  icon={getPlaceMarkerIcon(count, isSelected)}
							  eventHandlers={{
								click: () => {
								  setSelectedGroup(group);
								  setShowGroupModal(true);
								},
							  }}
							/>
						  );
						})}
				
				</MapContainer>
				<div
					  className="small text-muted mt-2"
					  style={{
						background: "rgba(255,255,255,0.9)",
						borderRadius: "8px",
						padding: "6px 10px",
						display: "inline-block",
					  }}
					>
					  <div><strong>Jelmagyarázat</strong></div>
					  <div>🔵 1–4 esemény</div>
					  <div>🟢 5–9 esemény</div>
					  <div>🟠 10+ esemény</div>
					</div>

			  </div>
            </>
          )}
        </Col>
      </Row>
	  
{/* Admin blokk: hiányzó koordinátájú események */}
    {!hideAdmin && eventsWithoutCoords.length > 0 && (
      <div className="mt-4">
        <h2 className="h6">Admin: hiányzó koordinátájú események</h2>
        <p className="small text-muted">
          Ezek az események még nem látszanak a térképen. Kattints a gombra, hogy
          a LocationIQ alapján kitöltsük a koordinátákat.
        </p>

        {geoAdminMessage && (
          <p className="small text-muted mb-2">{geoAdminMessage}</p>
        )}

        <div className="small">
          {eventsWithoutCoords.slice(0, 20).map((ev) => (
            <div
              key={ev.id}
              className="d-flex justify-content-between align-items-center border-bottom py-1"
            >
              <div>
                <strong>{ev.title}</strong>
                {ev.location && (
                  <div className="text-muted">{ev.location}</div>
                )}
              </div>
              <Button
                size="sm"
                variant="outline-primary"
                disabled={isGeoAdminBusy}
                onClick={() => geocodeEvent(ev)}
              >
                Koordináták kitöltése
              </Button>
            </div>
          ))}

          {eventsWithoutCoords.length > 20 && (
            <p className="small text-muted mt-2">
              Még {eventsWithoutCoords.length - 20} eseménynek hiányzik a
              koordinátája...
            </p>
          )}
        </div>
      </div>
    )}
	
      {/* Esemény modál – részletesebb nézet + link az oldalon lévő eseményre */}
      {selectedGroup && (
  <Modal
    show={showGroupModal}
    onHide={() => setShowGroupModal(false)}
    size="lg"
  >
    <Modal.Header closeButton>
      <Modal.Title>Események ezen a helyszínen</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <p className="text-muted mb-3">
        {selectedGroup.events[0].location}
      </p>

      {selectedGroup.events.map((ev) => (
        <div
          key={ev.id}
          className="border rounded p-2 mb-2"
          style={{ background: "#f8f9fa" }}
        >
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div className="fw-bold">{ev.title}</div>
              <div className="small text-muted">
                {formatDateRange(ev.start_date, ev.end_date)}
              </div>
            </div>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => {
                setSelectedEvent(ev);
                setShowEventModal(true);
                setShowGroupModal(false);
              }}
            >
              Részletek
            </Button>
          </div>
        </div>
      ))}
    </Modal.Body>
    <Modal.Footer>
      <Button
        variant="secondary"
        onClick={() => setShowGroupModal(false)}
      >
        Bezárás
      </Button>
    </Modal.Footer>
  </Modal>
)}

{selectedEvent && (
  <Modal
    show={showEventModal}
    onHide={() => {
      setShowEventModal(false);
      setSelectedEvent(null);
    }}
    size="lg"
  >
    <Modal.Header closeButton>
      <Modal.Title>{selectedEvent.title}</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {selectedEvent.location && (
        <p className="mb-2">
          <strong>Helyszín:</strong> {selectedEvent.location}
        </p>
      )}

      {(selectedEvent.start_date || selectedEvent.end_date) && (
        <p className="mb-2">
          <strong>Időpont:</strong>{" "}
          {formatDateRange(selectedEvent.start_date, selectedEvent.end_date)}
        </p>
      )}

      {selectedEvent.target_group && (
        <p className="mb-2">
          <strong>Célcsoport:</strong> {selectedEvent.target_group}
        </p>
      )}

      {selectedEvent.communities && selectedEvent.communities.name && (
        <p className="mb-2">
          <strong>Szervező / közösség:</strong>{" "}
          {selectedEvent.communities.name}
        </p>
      )}

      {selectedEvent.description && (
        <p className="mb-2" style={{ whiteSpace: "pre-line" }}>
          {selectedEvent.description}
        </p>
      )}

      {selectedEvent.registration_link && (
        <p className="mb-2">
          <a
            href={selectedEvent.registration_link}
            target="_blank"
            rel="noreferrer"
          >
            Jelentkezés / bővebb információ
          </a>
        </p>
      )}

      {selectedEvent.contact && (
        <p className="mb-2">
          <strong>Kapcsolat:</strong> {selectedEvent.contact}
        </p>
      )}
    </Modal.Body>
	<Modal.Footer className="d-flex justify-content-between align-items-center flex-wrap gap-2">
  <div className="d-flex gap-2 flex-wrap">
    <Button
      as="a"
      href={buildLandingUrl(selectedEvent)}
      target="_blank"
      rel="noreferrer"
      variant="outline-primary"
      size="sm"
    >
      Esemény megnyitása az oldalon
    </Button>

    {selectedEvent.lat != null &&
      selectedEvent.lng != null && (
        <Button
          as="a"
          href={`https://www.google.com/maps/dir/?api=1&destination=${selectedEvent.lat},${selectedEvent.lng}`}
          target="_blank"
          rel="noreferrer"
          variant="outline-secondary"
          size="sm"
        >
          Útvonaltervezés (Google Maps)
        </Button>
      )}
  </div>

		<Button
			variant="secondary"
			size="sm"
			onClick={() => {
			  setShowEventModal(false);
			  setSelectedEvent(null);
			}}
		  >
		  Bezárás
		</Button>
</Modal.Footer>


  </Modal>
)}


    </Container>

  );
};
export default MapBetaPage;
